import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import triggersHelper from "../../../../API/Triggers/triggersHelper";
import authorisationsHelper from "../../../../API/Authorisations/authorisationsHelper";
import {
  loadTriggerCatalog,
  findNativeEvent,
  findProvider,
  findProviderEvent,
} from "../../../../Constants/triggerCatalog";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import { parseEventConfig } from "./TriggersSettings";

export const buildInitialValues = () => ({
  name: "",
  description: "",
  agentID: "",
  customPromptID: "",
  forcedPrompt: "",
  eventSourceType: "NATIVE",
  native: {
    eventType: "document.created",
    eventCategory: "documents",
    dataFilter: [],
  },
  external: {
    provider: "",
    providerEvent: "",
    authorisationType: null,
    authorisationID: "",
    acquisitionMode: "POLLING",
    pollConfig: { intervalSeconds: 300 },
  },
  isActive: true,
});

export const fromTriggerRecord = (record) => {
  const base = buildInitialValues();
  if (!record) return base;
  const cfg = parseEventConfig(record.eventConfig);
  return {
    ...base,
    id: record.id,
    name: record.name,
    description: record.description,
    agentID: record.agentID,
    customPromptID: record.customPromptID || "",
    forcedPrompt: record.forcedPrompt || "",
    eventSourceType: record.eventSourceType,
    isActive: record.isActive,
    native:
      record.eventSourceType === "NATIVE"
        ? {
            eventType: cfg.eventType || base.native.eventType,
            eventCategory: cfg.eventCategory || base.native.eventCategory,
            dataFilter: cfg.dataFilter || [],
          }
        : base.native,
    external:
      record.eventSourceType === "EXTERNAL"
        ? {
            provider: cfg.provider || "",
            providerEvent: cfg.providerEvent || "",
            authorisationType: cfg.authorisationType || null,
            authorisationID: cfg.authorisationID || "",
            acquisitionMode: cfg.acquisitionMode || "POLLING",
            pollConfig: {
              intervalSeconds: cfg.pollConfig?.intervalSeconds || 300,
              endpoint: cfg.pollConfig?.endpoint || "",
              jsonPath: cfg.pollConfig?.jsonPath || "",
            },
          }
        : base.external,
  };
};

const TriggersList = ({ isFunctionsLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showChoice, setShowChoice] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [displayedTriggers, setDisplayedTriggers] = useState([]);
  const [selectedTriggerIds, setSelectedTriggerIds] = useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = useState(false);
  const [agentFilter, setAgentFilter] = useState("all");
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/triggers/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);

  const columns = [
    { title: t("settings.list.id"), width: 0.00001 },
    { title: t("settings.list.name"), width: 0.18 },
    { title: t("triggers.source"), width: 0.1 },
    { title: t("triggers.event"), width: 0.22 },
    { title: t("settings.list.agent"), width: 0.13 },
    { title: t("settings.list.status"), width: 0.1 },
    { title: t("triggers.lastTriggered"), width: 0.13 },
    { title: t("settings.list.actions"), isAction: true, width: 0.1 },
  ];

  const applyFilters = useCallback(() => {
    let filtered = [...(state?.triggers || [])];
    if (agentFilter && agentFilter !== "all") {
      filtered = filtered.filter((item) => item.agentID === agentFilter);
    }
    setDisplayedTriggers(filtered);
  }, [state?.triggers, agentFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleTriggerItemSelection = ({ id, item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !id) return;
    history.push(`/workspaces/${wsId}/triggers/${id}`);
  };

  const loadTriggers = async (workspaceID) => {
    try {
      setIsLoading(true);
      let resultTriggers = await triggersHelper.getTriggersByWorkspaceID(
        workspaceID
      );
      resultTriggers = resultTriggers?.data?.triggersByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );
      dispatch("SET_TRIGGERS", resultTriggers || []);
    } catch (error) {
      console.error("Error loading triggers:", error);
      dispatch("SET_TRIGGERS", []);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAuthorisations = async (workspaceID) => {
    try {
      const authorisationsData = await authorisationsHelper.getAuthorisations(
        workspaceID,
        null
      );
      let authorisations =
        authorisationsData?.data?.authorisationsByWorkspace?.items;
      authorisations =
        authorisations
          ?.filter((u) => u?._deleted !== true)
          ?.map((u) => {
            if (u?.staticArgs) {
              try {
                return { ...u, staticArgs: JSON.parse(u?.staticArgs) };
              } catch {
                return u;
              }
            }
            return u;
          }) || [];
      if (!state?.authorisations || state.authorisations.length === 0) {
        dispatch("SET_AUTHORISATIONS", authorisations || []);
      }
    } catch (error) {
      console.error("Error loading authorisations for triggers:", error);
    }
  };

  const loadCatalog = async () => {
    try {
      const catalog = await loadTriggerCatalog();
      dispatch("SET_TRIGGER_CATALOG", {
        version: catalog.version,
        nativeEvents: catalog.nativeEvents,
        providers: catalog.providers,
      });
    } catch (error) {
      console.error("Error loading trigger catalog:", error);
    }
  };

  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadTriggers(getActiveWorkspace()?.id);
      loadAuthorisations(getActiveWorkspace()?.id);
    }
    loadCatalog();
  }, [getActiveWorkspace()?.id]);

  const handleTriggerItemDeletion = async () => {
    try {
      setShowChoice(false);
      const _id = selectedItem?.id;
      if (_id) {
        await triggersHelper.deleteTrigger(_id);
        const workspaceID = getActiveWorkspace()?.id;
        if (workspaceID) await loadTriggers(workspaceID);
        AppToasty.show(t("triggers.deletionSuccess"), { type: "success" });
        setSelectedIndex(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("triggers.deletionFailed"), { type: "danger" });
    }
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedTriggerIds) {
        await triggersHelper.deleteTrigger(id);
      }
      const workspaceID = getActiveWorkspace()?.id;
      if (workspaceID) await loadTriggers(workspaceID);
      setSelectedTriggerIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("triggers.deletionSuccess"), { type: "success" });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("triggers.deletionFailed"), { type: "danger" });
    }
  };

  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/triggers/new`);
  };

  const getAgentName = (agentID) => {
    const agent = state?.agents?.find((a) => a.id === agentID);
    return agent?.label || t("settings.list.unknownAgent");
  };

  const formatEventLabel = (item) => {
    const cfg = parseEventConfig(item?.eventConfig);
    if (item?.eventSourceType === "NATIVE") {
      const evt = findNativeEvent(cfg.eventType);
      return evt ? `${evt.label}` : cfg.eventType || "—";
    }
    const provider = findProvider(cfg.provider);
    const evt = findProviderEvent(cfg.provider, cfg.providerEvent);
    return `${provider?.label || cfg.provider} · ${
      evt?.label || cfg.providerEvent || "—"
    }`;
  };

  const formatLastTriggered = (lastTriggeredAt) => {
    if (!lastTriggeredAt) return t("settings.list.notSet");
    return new Date(lastTriggeredAt).toLocaleString();
  };

  const _coreChoice = useMemo(
    () => (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("triggers.deleteConfirmation")}
        subCaption={t("triggers.deleteConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleTriggerItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    ),
    [setShowChoice, showChoice, selectedItem]
  );

  const _bulkDeleteChoice = useMemo(
    () => (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("triggers.deleteConfirmation"),
        })}
        subCaption={t("triggers.deleteConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    ),
    [showBulkDeleteChoice, selectedTriggerIds]
  );

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("triggers.loadingTriggers")}
        />
      ) : (
        <>
          {displayedTriggers.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("triggers.toolbar.searchTriggers")}
              onSelectionChange={(selectedItems) => {
                setSelectedTriggerIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedTriggerIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              actionButtons
              mapIconWithStatus={() => AppAssets.icons.orbit}
              mainActionLabel={t("settings.list.view")}
              showDropDownMenuForActions
              onClickMainAction={(item, index) =>
                handleTriggerItemSelection({ id: item?.id, item, index })
              }
              onActionItemPress={async (id, item, index) => {
                if (id === "EDIT") {
                  handleTriggerItemSelection({ id: item?.id, item, index });
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              isItemActionable={(item) => !!item?.item}
              style={{
                zIndex: -1,
                marginTop: 15,
                height:
                  displayedTriggers?.length >= 50 ? height - 95 : height - 75,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedTriggers
                ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                ?.map((u) => ({
                  id: u?.id || " ",
                  name: u?.name || " ",
                  source:
                    u?.eventSourceType === "NATIVE"
                      ? t("triggers.native")
                      : t("triggers.external"),
                  event: formatEventLabel(u),
                  agent: getAgentName(u?.agentID),
                  status: u?.status || "PENDING",
                  lastTriggered: formatLastTriggered(u?.lastTriggeredAt),
                  actions: " ",
                }))}
            >
              <AppDropDown
                showTextInputLabel
                label={t("settings.toolbar.agent")}
                inputLabelStyle={{
                  marginBottom: 0,
                  marginLeft: 5,
                  marginRight: 10,
                }}
                dropDownStyle={{
                  minWidth: 184,
                  maxWidth: 184,
                  textAlign: "flex-start",
                  borderColor: currentTheme?.theme?.border_color,
                }}
                wrapperContainer={{
                  flexDirection: "row",
                }}
                inputLabelFontWeight={"normal"}
                isElevated={false}
                items={[
                  { label: t("settings.toolbar.noFilter"), value: "all" },
                  ...(
                    state?.agents?.filter((agent) => agent?.mode !== "voice") ||
                    []
                  ).map((agent) => ({
                    label:
                      agent.label ||
                      agent.name ||
                      t("settings.toolbar.unknown"),
                    value: agent.id,
                  })),
                ]}
                selectedValue={agentFilter}
                onValueChange={(itemValue) => {
                  setAgentFilter(itemValue);
                }}
              />
              <ToothFairyButton
                title={t("common.create")}
                onPress={handleCreation}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 80,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.orbit}
              mainCaption={t("triggers.noTriggersFound")}
              subCaption={t("triggers.noTriggersFoundSub")}
              actionLabel={t("common.create")}
              onActionPress={handleCreation}
            />
          )}
        </>
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default TriggersList;
